import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Search, PackageCheck, Banknote, Gem, Layers, Box, Star, ShieldCheck, LineChart, Lock, FileCheck } from "lucide-react";
import Reveal from "@/components/Reveal";
import SellerForm from "@/components/SellerForm";
import Estimator from "@/components/Estimator";
import Testimonials from "@/components/Testimonials";
import Marquee from "@/components/Marquee";

const howItWorks = [
  { Icon: ClipboardList, title: "Tell the Vault what you hold", body: "Fill the form below with the collection type, rough size or value, and your location. Attach photos or a card list — even phone pics of the highlights help us price accurately." },
  { Icon: Search, title: "Ashton evaluates, personally", body: "Every submission is reviewed by a human collector within 24 hours. Big or unusual collections may get follow-up questions so nothing valuable is missed." },
  { Icon: PackageCheck, title: "Ship on us, fully insured", body: "Accept the offer and receive prepaid, insured FedEx/UPS labels plus a packing guide. Your cards are covered from your doorstep to the Vault." },
  { Icon: Banknote, title: "Get paid within 24 hours", body: "After verification, payout is sent fast — typically within 24 hours — via your preferred method. No consignment limbo, no waiting for a buyer." },
];

const whatWeBuy = [
  { Icon: Layers, title: "Magic: The Gathering", body: "Alpha/Beta relics, Reserved List, dual lands, Commander staples, and complete collections of any era." },
  { Icon: Star, title: "Pokémon", body: "Base Set and vintage holos, 1st editions, modern alt-arts, Japanese exclusives, and graded grails." },
  { Icon: Gem, title: "High-Value Singles", body: "Individual chase cards — raw or slabbed — across MTG, Pokémon, Yu-Gi-Oh!, Lorcana, and other TCGs." },
  { Icon: Box, title: "Sealed Product", body: "Booster boxes, Elite Trainer Boxes, cases, and vintage sealed — bought by the box or the pallet." },
];

const pricing = [
  { Icon: LineChart, title: "Market-anchored offers", body: "We price against TCGplayer market data and recent sold comps — the same numbers you can look up yourself." },
  { Icon: FileCheck, title: "Transparent breakdowns", body: "For larger collections you get a line-item summary of how the offer was built. Question any line; we'll walk it together." },
  { Icon: ShieldCheck, title: "No-obligation, always", body: "Every quote is free and pressure-free. Decline and nothing changes — your collection, your call." },
];

const security = [
  { Icon: Lock, title: "Insured door to door", body: "Prepaid labels carry full insurance coverage for the agreed offer value, with tracking at every checkpoint." },
  { Icon: ShieldCheck, title: "Verification you can watch", body: "Collections are opened, counted, and verified on intake. If anything differs from the description, we contact you before adjusting a cent." },
  { Icon: Banknote, title: "Fast, traceable payout", body: "Payment via your preferred method within 24 hours of verification — with a paper trail for every dollar." },
];

const Sell = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Sell Your Collection — Aardvarks Vault";
    if (location.state?.scrollToForm || location.hash === "#offer-form") {
      setTimeout(() => document.getElementById("offer-form")?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [location]);

  return (
    <div data-testid="sell-page">
      {/* HERO */}
      <section className="px-5 pb-16 pt-40 sm:px-8 lg:pt-48" data-testid="sell-hero">
        <div className="mx-auto max-w-4xl text-center">
          <motion.img
            src="/brand/explorer.png"
            alt="Archie the aardvark explorer unlocking the vault"
            className="mx-auto mb-8 w-40 animate-floaty sm:w-48"
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="sell-hero-explorer"
          />
          <p className="eyebrow">Sell Your Collection</p>
          <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="sell-hero-title">
            {["Turn your TCG collection", "into instant vault treasure."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-gold" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.1 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-cream/80"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
          >
            Fair market pricing, insured nationwide shipping, and payout in as little as 24 hours. One form stands
            between your cards and their next adventure.
          </motion.p>
        </div>
      </section>

      <Marquee />

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="sell-how-section">
        <Reveal>
          <p className="eyebrow">How It Works</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            From shoebox to payout in four moves
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {howItWorks.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="card-frame foil-sheen flex h-full gap-6 p-8" data-testid={`sell-step-${i}`}>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                  <s.Icon size={22} className="text-gold" />
                </div>
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint">Step {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-1 font-serif text-lg font-semibold text-cream">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/70">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE BUY */}
      <section id="what-we-buy" className="border-y border-gold/10 bg-ink/40 py-24" data-testid="sell-buy-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">What We Buy</p>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              If it's treasure, the Vault wants it
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whatWeBuy.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-mint/20 bg-navy/60 p-7 transition-transform duration-300 hover:-translate-y-1" data-testid={`buy-category-${i}`}>
                  <w.Icon size={26} className="text-mint" />
                  <h3 className="mt-5 font-serif text-lg font-semibold text-cream">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/70">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="sell-pricing-section">
        <Reveal>
          <p className="eyebrow">Evaluations & Fair Pricing</p>
          <h2 className="mt-4 max-w-2xl font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Offers you can fact-check, not just take on faith
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {pricing.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div className="card-frame foil-sheen h-full p-8" data-testid={`pricing-card-${i}`}>
                <p.Icon size={28} className="text-gold" />
                <h3 className="mt-5 font-serif text-lg font-semibold text-gold">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/75">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECURITY */}
      <section className="border-y border-plum/25 bg-gradient-to-r from-ink via-navy to-ink py-24" data-testid="sell-security-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Secure Transactions</p>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Your cards travel like treasure — because they are
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {security.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="h-full rounded-2xl border border-cream/10 bg-navy/70 p-8" data-testid={`security-card-${i}`}>
                  <s.Icon size={28} className="text-mint" />
                  <h3 className="mt-5 font-serif text-lg font-semibold text-cream">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ESTIMATOR */}
      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8" data-testid="sell-estimator-section">
        <Reveal className="text-center">
          <p className="eyebrow">Before You Commit</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            What might your hoard be worth?
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="mt-12">
          <Estimator />
        </Reveal>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-gold/10 bg-ink/40 py-24" data-testid="sell-testimonials-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="eyebrow">Seller Stories</p>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Sellers who already opened the vault
            </h2>
          </Reveal>
          <Testimonials />
        </div>
      </section>

      {/* FORM */}
      <section id="offer-form" className="mx-auto max-w-4xl scroll-mt-28 px-5 py-24 sm:px-8" data-testid="sell-form-section">
        <Reveal className="text-center">
          <p className="eyebrow">Get a Collection Offer</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Open the vault door
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-cream/70 sm:text-base">
            Two minutes of details, a few photos, and Ashton replies within 24 hours with a real number.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <div className="relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2" aria-hidden="true">
              <div className="keyhole opacity-80" />
            </div>
            <SellerForm />
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Sell;
